import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  ListGroup,
  Alert,
  Spinner,
} from "react-bootstrap";
import "./QuizAttempt.css";
import { useTranslation } from "react-i18next"; // Importer useTranslation

const QuizAttempt = ({ lesson }) => {
  const [attempt, setAttempt] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [userAnswers, setUserAnswers] = useState([]);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userAttempts, setUserAttempts] = useState([]);
  const [isQuizEmpty, setIsQuizEmpty] = useState(false);
  const [header, setHeader] = useState("Quiz");
  const { t } = useTranslation(); // Assigner useTranslation à {t}

  // Vérifier si le quiz est vide
  useEffect(() => {
    const checkIfQuizIsEmpty = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/quizzes/${lesson.quiz}/is-empty`,
          {
            method: "GET",
            credentials: "include",
          }
        );

        const data = await response.json();

        if (response.ok) {
          setIsQuizEmpty(data.isEmpty);
          setHeader(data.header);
        } else {
          throw new Error(data.message || t("fetch_error_qst"));
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    checkIfQuizIsEmpty();
  }, [lesson.quiz]);

  // Effacer les réponses sélectionnées lorsque la question change
  useEffect(() => {
    if (selectedQuestion && attempt) {
      const completedQuestion = attempt.completed_questions.find(
        (cq) => cq.question === selectedQuestion._id
      );
      if (completedQuestion) {
        setUserAnswers(completedQuestion.answers);
      } else {
        setUserAnswers([]);
      }
    }
  }, [selectedQuestion, attempt]);

  useEffect(() => {
    const fetchAttempt = async () => {
      setError("");
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/quiz-attempts/current`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ quizId: lesson.quiz }),
            credentials: "include",
          }
        );

        const data = await response.json();

        if (response.ok) {
          setAttempt(data.userQuizAttempt);
          const quizQuestions = data.userQuizAttempt.quiz.questions || [];
          setQuestions(quizQuestions);
          setSelectedQuestion(
            quizQuestions.length > 0 ? quizQuestions[0] : null
          );
        } else if (response.status === 404) {
          setAttempt(null);
        } else {
          throw new Error(data.message || "Une erreur est survenue");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAttempt();
  }, [lesson.quiz]);

  useEffect(() => {
    const fetchUserAttempts = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/quiz-attempts/${lesson.quiz}/attempts`,
          {
            method: "GET",
            credentials: "include",
          }
        );

        const data = await response.json();

        if (response.ok) {
          setUserAttempts(data.attempts);
        } else {
          throw new Error(data.message || "Une erreur est survenue");
        }
      } catch (err) {
        setError(err.message);
      }
    };

    fetchUserAttempts();
  }, [lesson.quiz]);

  const handleStartAttempt = async () => {
    setError("");
    try {
      setIsLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/quiz-attempts/create`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ quizId: lesson.quiz }),
          credentials: "include",
        }
      );

      const data = await response.json();

      if (response.ok) {
        const quizQuestions = data.attempt.quiz.questions || [];
        setAttempt(data.attempt);
        setQuestions(quizQuestions);
        setSelectedQuestion(quizQuestions.length > 0 ? quizQuestions[0] : null);
      } else {
        throw new Error(data.message || t("start_attempt_error"));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitAnswer = async () => {
    if (!selectedQuestion || userAnswers.length === 0) {
      setError(t("select_answer_error"));
      return;
    }

    setError("");
    try {
      setIsSubmitting(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/quiz-attempts/submit-question`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            attemptId: attempt._id,
            questionId: selectedQuestion._id,
            answers: userAnswers,
          }),
          credentials: "include",
        }
      );

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage(t("answer_submitted_success"));
        setAttempt((prev) => ({
          ...prev,
          completed_questions: data.completed_questions,
        }));
      } else {
        throw new Error(data.message || t(""));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitQuiz = async () => {
    setError("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/quiz-attempts/submit`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ attemptId: attempt._id }),
          credentials: "include",
        }
      );

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage(t("quiz_submitted_success "));
        setAttempt(null);

        const attemptsResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/quiz-attempts/${lesson.quiz}/attempts`,
          {
            method: "GET",
            credentials: "include",
          }
        );

        const attemptsData = await attemptsResponse.json();

        if (attemptsResponse.ok) {
          setUserAttempts(attemptsData.attempts);
        } else {
          throw new Error(attemptsData.message || t("fetch_attempts_error"));
        }
      } else {
        throw new Error(data.message || t("fetch_attempts_error"));
      }
    } catch (err) {
      setError(err.message);
    }
  };

  if (isLoading) {
    return (
      <Container className="d-flex align-items-center justify-content-center vh-100">
        <Spinner animation="border" />
      </Container>
    );
  }

  if (isQuizEmpty) {
    return (
      <Container className="d-flex align-items-center justify-content-center vh-100">
        <div className="text-center">
          <h3 className="text-muted">{t("quiz_empty")}</h3>
        </div>
      </Container>
    );
  }

  if (!attempt) {
    return (
      <Container className="d-flex align-items-center justify-content-center vh-100">
        <div className="quiz-start-container">
          <h3 className="quiz-start-title mb-4">{t("quiz_default_header")}</h3>

          {error && <Alert variant="danger">{error}</Alert>}

          <Button
            onClick={handleStartAttempt}
            variant="primary"
            className="quiz-start-btn"
          >
            {t("start_new_attempt")}
          </Button>

          <div className="quiz-attempts-list">
            <h5 className="mt-4">{t("previous_attempts")}</h5>
            <ListGroup>
              {userAttempts.map((attempt, index) => (
                <ListGroup.Item key={attempt._id} className="quiz-attempt-item">
                  <strong>
                    {t("attempt")} {index + 1}
                  </strong>{" "}
                  -
                  <span className="text-muted">
                    {" "}
                    {t("start")} :{" "}
                    {new Date(attempt.created_at).toLocaleString("fr-FR")}
                  </span>{" "}
                  -
                  <span className="fw-bold">
                    {" "}
                    {t("score")} :{" "}
                    {attempt.score !== undefined ? `${attempt.score}%` : "N/A"}
                  </span>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container className="quiz-container">
      <h1 className="text-center mb-2 mt-2">{header}</h1>
      <hr className=""></hr>
      <Row>
        <Col md={4} className="border-end">
          <h4 className="quiz-header mb-3">{t("questions")}</h4>
          <ListGroup className="question-list">
            {questions.map((q, index) => {
              const isAnswered = attempt.completed_questions.some(
                (cq) => cq.question === q._id
              );

              return (
                <ListGroup.Item
                  key={q._id}
                  onClick={() => setSelectedQuestion(q)}
                  className={`question-item d-flex justify-content-between 
                    ${selectedQuestion?._id === q._id ? "selected" : ""}
                    ${isAnswered ? "answered" : ""}`}
                >
                  <span>
                    {t("question")} {index + 1}
                  </span>
                  {isAnswered && (
                    <span className="text-success fw-bold">✔</span>
                  )}
                </ListGroup.Item>
              );
            })}
          </ListGroup>
        </Col>

        <Col md={8}>
          {selectedQuestion ? (
            <>
              <h4 className="mb-3 text-center">{selectedQuestion.question}</h4>
              <ListGroup>
                {selectedQuestion.answers.map((answer, index) => (
                  <ListGroup.Item key={index} className="answer-option">
                    <input
                      type="checkbox"
                      value={answer}
                      onChange={(e) => {
                        const value = e.target.value;
                        setUserAnswers((prev) =>
                          prev.includes(value)
                            ? prev.filter((a) => a !== value)
                            : [...prev, value]
                        );
                      }}
                      checked={userAnswers.includes(answer)}
                      disabled={attempt.completed_questions.some(
                        (cq) => cq.question === selectedQuestion._id
                      )}
                    />
                    {answer}
                  </ListGroup.Item>
                ))}
              </ListGroup>
              {!attempt.completed_questions.some(
                (cq) => cq.question === selectedQuestion._id
              ) && (
                <Button
                  onClick={handleSubmitAnswer}
                  disabled={isSubmitting}
                  className="mt-3 submit-btn btn-primary"
                >
                  {isSubmitting ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    t("submit_answer")
                  )}
                </Button>
              )}
            </>
          ) : (
            <p className="text-muted text-center mt-4">
              {t("select_question")}
            </p>
          )}
        </Col>
      </Row>

      <Row className="mt-4">
        <Col className="text-end">
          <Button
            onClick={handleSubmitQuiz}
            variant="success"
            className="submit-btn"
          >
            {t("submit_quiz")}
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" className="mt-3 alert-message">
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert variant="success" className="mt-3 alert-message">
          {successMessage}
        </Alert>
      )}
    </Container>
  );
};

export default QuizAttempt;
